window.wpasInitCarousel = function (sectionId, options) {
  var opts = options || {};
  var root = document.getElementById(sectionId);
  if (!root || root.dataset.wpasInit === 'true') return;
  root.dataset.wpasInit = 'true';

  var track = root.querySelector('.wpas-track');
  var cards = Array.prototype.slice.call(root.querySelectorAll('.wpas-card'));

  // Lazy-load video sources (and their posters) only once a card is about
  // to scroll into view, instead of every video - including the offscreen
  // duplicate copies rendered for the seamless loop - downloading on page
  // load. Markup ships with `data-src` on each <source> and `data-poster`
  // on the video instead of the real `src`/`poster`, so nothing is
  // requested until this observer fires.
  function lazyLoadVideo(card) {
    var video = card.querySelector('.wpas-video[data-lazy="true"]');
    if (!video) return;
    var poster = video.getAttribute('data-poster');
    if (poster) {
      video.setAttribute('poster', poster);
      video.removeAttribute('data-poster');
    }
    var sources = video.querySelectorAll('source[data-src]');
    if (sources.length) {
      sources.forEach(function (source) {
        source.setAttribute('src', source.getAttribute('data-src'));
        source.removeAttribute('data-src');
      });
      video.load();
    }
    video.removeAttribute('data-lazy');
  }
  if ('IntersectionObserver' in window) {
    var lazyObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          lazyLoadVideo(entry.target);
          lazyObserver.unobserve(entry.target);
        });
      },
      // Generous rootMargin so a video is already downloaded/decoded by
      // the time its card actually reaches center and autoplay tries to
      // play it, not just when it first peeks onscreen.
      { root: null, rootMargin: '400px', threshold: 0.01 }
    );
    cards.forEach(function (card) { lazyObserver.observe(card); });
  } else {
    // No IntersectionObserver support - fall back to loading immediately
    // rather than never loading at all.
    cards.forEach(lazyLoadVideo);
  }

  // Arrows
  var prevBtn = root.querySelector('.wpas-arrow--prev');
  var nextBtn = root.querySelector('.wpas-arrow--next');

  function scrollByCard(dir) {
    if (!track || !cards.length) return;
    // Center on the actual next/previous card's real measured position -
    // the same getBoundingClientRect-based math centerCard (and therefore
    // card-click, which already lands perfectly) uses - instead of blindly
    // adding one fixed "cardWidth + 8" guess. Any drift between that guess
    // and the card's real rendered width/gap is exactly what was landing
    // the arrow scroll "in between" two cards instead of exactly centered.
    var current = getClosestCard();
    var idx = current ? cards.indexOf(current) : -1;
    var target = idx !== -1 ? cards[idx + dir] : null;
    if (!target) return;
    centerCard(target, false);
  }
  if (prevBtn) prevBtn.addEventListener('click', function () { pauseAutoSlide(); scrollByCard(-1); });
  if (nextBtn) nextBtn.addEventListener('click', function () { pauseAutoSlide(); scrollByCard(1); });

  // Runs once a JS-driven swipe animation has finished moving the track:
  // restores native scroll-snap for whatever comes next (drag, autoslide,
  // another swipe).
  function onSwipeSettled() {
    track.style.scrollSnapType = '';
  }

  // Our own smooth-scroll tween (instead of the browser's native
  // `behavior: 'smooth'`) so that looping/wrapping can safely correct
  // track.scrollLeft mid-animation. Mixing a native smooth-scroll with a
  // manual scrollLeft jump makes the browser fight the jump and rubber-band
  // back toward it - which is what caused the carousel to snap back to a
  // blank edge instead of looping when scrolling left.
  var scrollAnimId = null;
  function easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }
  // Starts fast, decelerates gradually and stays slow for a longer tail than
  // easeInOutQuad. Used for arrow-triggered swipes: since the card's
  // scale/opacity are driven directly off scroll progress (see
  // updateActiveCard), spending more of the animation's time in that slow
  // final stretch is what makes the incoming card feel like it "settles"
  // and zooms into place gently, instead of scaling up at a constant rate.
  function easeOutCubic(t) {
    var p = t - 1;
    return p * p * p + 1;
  }
  function animateScrollTo(target, duration, easingFn, onComplete) {
    if (!track) return;
    var ease = easingFn || easeInOutQuad;
    if (scrollAnimId) window.cancelAnimationFrame(scrollAnimId);
    var start = track.scrollLeft;
    var change = target - start;
    var startTime = null;
    function tick(ts) {
      if (startTime === null) startTime = ts;
      var progress = Math.min((ts - startTime) / duration, 1);
      track.scrollLeft = start + change * ease(progress);
      var delta = wrapIfNeeded();
      if (delta) {
        // The position we just landed on wrapped to the equivalent spot in
        // another copy - shift our animation baseline by the same amount so
        // the next frame continues smoothly from there instead of snapping
        // back to the pre-wrap position.
        start += delta;
      }
      // Drive the zoom/fade directly off this animation frame rather than
      // waiting on the browser's (often throttled/coalesced) scroll event -
      // that mismatch was the source of the "jhatka" jump.
      updateActiveCard();
      if (progress < 1) {
        scrollAnimId = window.requestAnimationFrame(tick);
      } else {
        scrollAnimId = null;
        if (onComplete) onComplete();
      }
    }
    scrollAnimId = window.requestAnimationFrame(tick);
  }

  // Keep the visually-centered card scaled up / fully opaque, and its
  // neighbours dimmed down, to match the "center-peek" carousel look.
  // This considers every rendered copy of a card (including the duplicate
  // copies used for the seamless auto-slide loop), because whichever copy
  // is physically centered on screen is the one that should look active -
  // even if it's a duplicate.
  var activeUpdateScheduled = false;
  function scheduleActiveUpdate() {
    if (activeUpdateScheduled) return;
    activeUpdateScheduled = true;
    window.requestAnimationFrame(function () {
      activeUpdateScheduled = false;
      updateActiveCard();
    });
  }
  function updateActiveCard() {
    if (!track || !cards.length) return;
    // No freeze/delay of any kind here - scale and opacity are recalculated
    // from each card's REAL current position every single frame, in the
    // same tick as the position change itself. That's what guarantees swipe
    // and zoom are perfectly in sync with zero handoff: there's nothing to
    // hand off, it's one computation running continuously throughout the
    // whole swipe (and during drag/autoslide too).
    var trackRect = track.getBoundingClientRect();
    var trackCenter = trackRect.left + trackRect.width / 2;
    var cardWidth = (cards[0] && cards[0].getBoundingClientRect().width) || 240;
    var maxDist = cardWidth + 16; // ~one card-step away = fully "resting" state
    var closest = null;
    var closestDist = Infinity;

    cards.forEach(function (card) {
      var r = card.getBoundingClientRect();
      var cardCenter = r.left + r.width / 2;
      var dist = Math.abs(cardCenter - trackCenter);

      // Continuous, position-driven scale/opacity instead of a binary
      // active/inactive class flip - every card gradually zooms in and
      // fades up as it slides toward center, exactly in step with the
      // scroll itself, so there's never a sudden snap/jerk when a card
      // crosses the midpoint. This is recalculated every animation frame
      // (see the calls to updateActiveCard below), so it always matches
      // the true on-screen position, even mid smooth-scroll.
      var t = Math.min(dist / maxDist, 1);
      var scale = 1 - t * 0.15; // 1 (center) -> 0.85 (resting) - closer in size to the reference layout
      var opacity = 1 - t * 0.4; // 1 (center) -> 0.6 (resting) - closer in brightness to the reference layout
      card.style.transform = 'scale(' + scale.toFixed(3) + ')';
      card.style.opacity = opacity.toFixed(3);

      if (dist < closestDist) {
        closestDist = dist;
        closest = card;
      }
    });
    cards.forEach(function (card) {
      card.classList.toggle('is-active', card === closest);
    });
    syncActiveVideo(closest);
  }
  if (track) {
    track.addEventListener('scroll', scheduleActiveUpdate, { passive: true });
  }
  scheduleActiveUpdate();

  // Only the centered ("front") card's video should ever be playing -
  // every other card (including any peeking neighbours) stays paused, and
  // switches the moment a different card becomes centered.
  var sectionVisible = false;
  function syncActiveVideo(activeCard) {
    if (!opts.autoplay) return;
    cards.forEach(function (card) {
      var v = card.querySelector('.wpas-video');
      if (!v) return;
      if (card === activeCard) {
        if (sectionVisible && !v.wpasUserPaused) {
          // If this video hasn't been lazy-loaded yet (e.g. autoplay
          // catches up to a card faster than the 400px lazy rootMargin),
          // load it right now instead of silently failing to play.
          if (v.dataset.lazy === 'true') lazyLoadVideo(card);
          v.play().catch(function () {});
        }
      } else if (!v.paused) {
        v.pause();
      }
    });
  }
  if (opts.autoplay && 'IntersectionObserver' in window && root) {
    var sectionObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          sectionVisible = entry.isIntersecting;
        });
        var current = root.querySelector('.wpas-card.is-active') || cards[0];
        syncActiveVideo(current);
      },
      { threshold: 0.3 }
    );
    sectionObserver.observe(root);
  }

  // Re-check the wrap position and the centered card after any viewport
  // resize (rotation, window resize, etc.) so the loop keeps landing on
  // the correct spot and never drifts to a visible end.
  window.addEventListener('resize', function () {
    wrapIfNeeded();
    scheduleActiveUpdate();
  });

  // Smoothly scroll so the given card sits centered in the track.
  function centerCard(card, instant, onComplete, duration) {
    if (!track) return;
    var trackRect = track.getBoundingClientRect();
    var cardRect = card.getBoundingClientRect();
    var offset = (cardRect.left + cardRect.width / 2) - (trackRect.left + trackRect.width / 2);
    if (Math.abs(offset) < 1) {
      track.style.scrollSnapType = ''; // already centered - nothing to tween, just hand snap back
      if (onComplete) onComplete();
      return;
    }
    if (instant) {
      track.scrollLeft += offset;
      if (onComplete) onComplete();
    } else {
      track.style.scrollSnapType = 'none';
      animateScrollTo(track.scrollLeft + offset, duration || 550, easeOutCubic, function () {
        onSwipeSettled();
        if (onComplete) onComplete();
      });
    }
  }

  // Finds whichever card is currently physically nearest the track's center
  // - same "closest" logic updateActiveCard uses to pick the active card,
  // pulled out here so the manual-swipe handling below can also use it.
  function getClosestCard() {
    if (!track || !cards.length) return null;
    var trackRect = track.getBoundingClientRect();
    var trackCenter = trackRect.left + trackRect.width / 2;
    var closest = null;
    var closestDist = Infinity;
    cards.forEach(function (card) {
      var r = card.getBoundingClientRect();
      var dist = Math.abs(r.left + r.width / 2 - trackCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closest = card;
      }
    });
    return closest;
  }

  // Continuous seamless auto-slide loop.
  // The liquid template renders the cards twice back-to-back when this is on,
  // so the track can scroll through the first copy and silently wrap to 0
  // right as the duplicate copy lines up, giving an endless left-moving loop.
  var resumeTimer = null;
  var pausedByUser = false; // paused because of a real drag/touch/arrow/click interaction
  var autoSlideTimer = null; // pending "advance to next card" timeout
  var onAutoSlideResume = null; // wired up by the autoslide block below, so a pause can restart the step cycle once it ends

  function pauseAutoSlide() {
    pausedByUser = true;
    clearTimeout(autoSlideTimer); // cancel any pending advance so it doesn't fire mid-interaction
    clearTimeout(resumeTimer);
    resumeTimer = setTimeout(function () {
      pausedByUser = false;
      if (onAutoSlideResume) onAutoSlideResume();
    }, 2500);
  }

  var loopPasses = parseInt(track && track.dataset.loopPasses, 10) || 1;

  // With 3 rendered copies (before/main/after) the middle copy is the "real"
  // set. Scrolling can wrap seamlessly in EITHER direction: once the visible
  // window drifts into the before-copy or after-copy, we silently jump by
  // exactly one set-width so it lands back on the equivalent spot in the
  // main copy, giving an endless loop whether the user/auto-slide moves
  // left or right.
  function getSetWidth() {
    if (loopPasses !== 3 || !track || !cards.length) return 0;
    var perSet = cards.length / 3;
    var firstOfBefore = cards[0];
    var firstOfMain = cards[perSet];
    if (!firstOfBefore || !firstOfMain) return 0;
    // Measuring the offset between two corresponding cards (rather than
    // scrollWidth / 3) keeps the one-time centering padding at the very
    // start/end of the track out of the math, so jumping by this amount
    // lands the next copy's card in exactly the same centered/peek spot.
    return firstOfMain.getBoundingClientRect().left - firstOfBefore.getBoundingClientRect().left;
  }
  // Returns the pixel delta applied (positive or negative) so callers that
  // are mid-animation can shift their own baseline by the same amount -
  // otherwise the wrap gets silently undone on the very next frame.
  function wrapIfNeeded() {
    var setWidth = getSetWidth();
    if (!setWidth) return 0;
    if (track.scrollLeft <= 0) {
      track.scrollLeft += setWidth;
      return setWidth;
    } else if (track.scrollLeft >= setWidth * 2) {
      track.scrollLeft -= setWidth;
      return -setWidth;
    }
    return 0;
  }

  // Position the very first frame on the centered "real" card instantly,
  // before revealing the track, so visitors never see the scrolled-left
  // (blank gutter) starting state flash on load.
  if (track) {
    track.classList.add('wpas-positioning');
    var revealTrack = function () {
      window.requestAnimationFrame(function () {
        if (loopPasses === 3) {
          track.scrollLeft = getSetWidth();
        }
        // second frame: layout is now guaranteed settled (fonts/images
        // reflow etc.), correct any tiny drift, then fade in.
        window.requestAnimationFrame(function () {
          if (loopPasses === 3) {
            track.scrollLeft = getSetWidth();
          }
          track.classList.remove('wpas-positioning');
          updateActiveCard();
        });
      });
    };
    revealTrack();
  }

  // Keep the loop wrap-check wired to every scroll event regardless of
  // whether continuous auto-slide is on - dragging/swiping should loop
  // endlessly too, not just the automatic movement.
  if (track) {
    track.addEventListener('scroll', wrapIfNeeded, { passive: true });
  }

  if (track && track.dataset.autoslide === 'true') {
    // How long (ms) a card stays centered - fully zoomed, fully at rest -
    // before the carousel advances to the next one. The section's
    // data-autoslide-interval attribute is authored in SECONDS (it comes
    // from a schema `range` setting with unit: "s"), so it must be
    // multiplied by 1000 here to get milliseconds - previously this value
    // was used as-is, so a setting of e.g. "3" produced a 3ms interval
    // instead of 3000ms, making the carousel advance almost instantly.
    // Falls back to a straight 4-second hold if the attribute is
    // missing/invalid, matching the requested "stay 4 seconds, then move
    // to the next card" behaviour.
    var stepInterval = (parseFloat(track.dataset.autoslideInterval) || 4) * 1000;

    // How long the actual hop to the next card takes. Deliberately slower
    // than the 550ms used for arrow/manual clicks (see centerCard calls
    // below) - a snappier swipe there feels responsive to a direct click,
    // but the same speed during unattended auto-slide reads as a "fast
    // jump" rather than a calm, deliberate one-by-one move. This only
    // affects the transition itself; the card still holds fully at rest
    // for the full stepInterval before and after each hop.
    var autoSlideStepDuration = 900;

    // Auto-slide only pauses on a real interaction: drag, touch, a card
    // click, or the arrow buttons - NOT on ordinary mouse-wheel / trackpad
    // page scrolling, and NOT on simply hovering, which used to (wrongly)
    // freeze/move the carousel.
    track.addEventListener('pointerdown', pauseAutoSlide);
    track.addEventListener('touchstart', pauseAutoSlide, { passive: true });

    var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReducedMotion) {
      var scheduleAutoSlideStep = function () {
        clearTimeout(autoSlideTimer);
        autoSlideTimer = setTimeout(runAutoSlideStep, stepInterval);
      };
      var runAutoSlideStep = function () {
        if (pausedByUser || !track) return;
        // Same precise "find the real next card and center on it" approach
        // scrollByCard uses now - not a fixed cardWidth+gap guess - so
        // auto-slide always lands exactly centered too.
        var current = getClosestCard();
        var idx = current ? cards.indexOf(current) : -1;
        // Wrap the index with modulo instead of a plain idx + 1, so once
        // the carousel reaches the last card it loops back to the first
        // one instead of getting permanently stuck (cards[idx + 1] was
        // undefined on the last card, so nothing ever moved again).
        var nextIdx = idx !== -1 ? (idx + 1) % cards.length : 0;
        var target = cards[nextIdx];
        if (!target) {
          scheduleAutoSlideStep();
          return;
        }
        centerCard(target, false, scheduleAutoSlideStep, autoSlideStepDuration); // hold here, fully zoomed, before the next hop
      };
      onAutoSlideResume = scheduleAutoSlideStep; // let pauseAutoSlide restart the cycle once its pause window elapses

      scheduleAutoSlideStep();

      // Stop the cycle if the section is removed (e.g. theme editor re-render).
      if ('MutationObserver' in window) {
        var bodyObserver = new MutationObserver(function () {
          if (!document.body.contains(root)) {
            clearTimeout(autoSlideTimer);
            clearTimeout(resumeTimer);
            bodyObserver.disconnect();
          }
        });
        bodyObserver.observe(document.body, { childList: true, subtree: true });
      }
    }
  }

  // Every rendered copy of every card (real + the duplicate loop copies)
  // gets fully working controls. They carry the exact same product/variant
  // data, so whichever copy happens to be on screen (real or duplicate)
  // must behave identically.
  cards.forEach(function (card) {
    var video = card.querySelector('.wpas-video');
    var muteBtn = card.querySelector('.wpas-mute');
    var playBtn = card.querySelector('.wpas-playpause');
    var fsBtn = card.querySelector('.wpas-fullscreen');
    var buyBtn = card.querySelector('.wpas-buy-now');

    // Click anywhere on a card (except its interactive controls) brings it
    // to the front by smoothly sliding it to center - this replaces any
    // hover-based movement, so simply resting the cursor over a card never
    // causes anything to move.
    card.addEventListener('click', function (e) {
      var isControl = e.target.closest(
        '.wpas-btn, .wpas-buy-now, .wpas-thumb-link, .wpas-title'
      );
      if (isControl) return;
      pauseAutoSlide();
      centerCard(card, false);
    });

    if (video) {
      // Mute toggle
      if (muteBtn) {
        var iconMuted = muteBtn.querySelector('.wpas-icon-muted');
        var iconUnmuted = muteBtn.querySelector('.wpas-icon-unmuted');
        muteBtn.addEventListener('click', function () {
          video.muted = !video.muted;
          muteBtn.setAttribute('aria-pressed', String(video.muted));
          if (iconMuted && iconUnmuted) {
            iconMuted.style.display = video.muted ? '' : 'none';
            iconUnmuted.style.display = video.muted ? 'none' : '';
          }
        });
      }

      // Play/pause toggle
      if (playBtn) {
        var iconPlay = playBtn.querySelector('.wpas-icon-play');
        var iconPause = playBtn.querySelector('.wpas-icon-pause');
        function syncPlayIcon() {
          var paused = video.paused;
          if (iconPlay && iconPause) {
            iconPlay.style.display = paused ? '' : 'none';
            iconPause.style.display = paused ? 'none' : '';
          }
          playBtn.setAttribute('aria-pressed', String(!paused));
        }
        playBtn.addEventListener('click', function () {
          // Pressing play manually on a not-yet-lazy-loaded video should
          // still work - load it immediately on demand.
          if (video.dataset.lazy === 'true') lazyLoadVideo(card);
          if (video.paused) {
            video.play();
            video.wpasUserPaused = false;
          } else {
            video.pause();
            video.wpasUserPaused = true;
          }
          syncPlayIcon();
        });
        video.addEventListener('play', syncPlayIcon);
        video.addEventListener('pause', syncPlayIcon);
      }

      // Fullscreen
      if (fsBtn) {
        fsBtn.addEventListener('click', function () {
          if (video.dataset.lazy === 'true') lazyLoadVideo(card);
          if (video.requestFullscreen) {
            video.requestFullscreen();
          } else if (video.webkitEnterFullscreen) {
            video.webkitEnterFullscreen();
          }
        });
      }
    }

    // Buy now - plain link straight to the product page. Just stop the
    // click from also triggering the card's center-on-click handler above;
    // the browser's normal navigation via href does the rest.
    if (buyBtn) {
      buyBtn.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    }
  });
};
